// components/LanguageSwitcher.jsx — AZ / EN toggle for the header.
//
// - Persists the choice via i18n/changeLanguage (writes localStorage:itemzo_lang
//   and updates <html lang>).
// - Admin-gated: reads /public/language-settings via useLanguageSettings.
//     * hidden when multi-language off, switcher hidden, or <2 switchable langs
//     * renders only active + showInSwitcher languages
//   While settings are still loading (or the API is unreachable) it falls back
//   to the existing bundled AZ/EN behaviour so the site keeps working.

import React from 'react'
import { useTranslation } from 'react-i18next'
import { changeLanguage, SUPPORTED_LANGS } from '../i18n'
import { useLanguageSettings } from '../i18n/useLanguageSettings'

const LanguageSwitcher = ({ className = '' }) => {
    const { i18n, t } = useTranslation()
    const settings = useLanguageSettings()

    // Admin says hide (effective flag already accounts for multiLang + count>1).
    if (settings && !settings.showLanguageSwitcher) return null

    // Which languages to offer: admin's active+switcher list, else bundled default.
    const codes = settings
        ? (settings.languages || []).map((l) => l.code).filter((c) => SUPPORTED_LANGS.includes(c))
        : SUPPORTED_LANGS
    if (codes.length < 2) return null

    const current = codes.includes(i18n.language) ? i18n.language : (settings?.defaultLanguage || 'az')

    return (
        <div
            role='group'
            aria-label={t('lang.switchAria')}
            className={`inline-flex items-center rounded-pill border border-slate-200 bg-white p-0.5 ${className}`}
        >
            {codes.map((lng) => {
                const active = current === lng
                return (
                    <button
                        key={lng}
                        type='button'
                        onClick={() => changeLanguage(lng)}
                        aria-pressed={active}
                        className={`inline-flex h-6 min-w-[28px] items-center justify-center rounded-pill px-2 text-[11px] font-bold uppercase transition-colors ${
                            active
                                ? 'bg-brand-600 text-white'
                                : 'text-slate-500 hover:text-brand-700'
                        }`}
                    >
                        {t(`lang.${lng}`)}
                    </button>
                )
            })}
        </div>
    )
}

export default LanguageSwitcher
